import { FC } from 'react';
import { Link } from 'react-router-dom';
import { Image } from '@chakra-ui/react';

import {
  FeatureFilterField,
  FeatureName,
  UserFilterValues,
} from '__generated__/GQL';
import safeImg from 'assets/brand/safe@2x.png';
import TeapotImg from 'assets/brand/teapot@2x.png';
import useCanCompanyUserAccessFeature from 'common/hooks/useCanCompanyUserAccessFeature';
import useCompanyContext from 'common/hooks/useCompanyContext';
import { useCompanyProfile } from 'common/hooks/useCompanyProfile';
import useMainBankAccountId from 'common/hooks/useMainBankAccountId';
import Button from 'components/_core/Button';
import Text from 'components/_core/Text';
import Loader from 'components/Loader';
import IbanPreview from 'features/Bank/components/IbanPreview';
import * as routes from 'features/PaymentCards/routes';

import { useTransactionsFilters } from '../../hooks/useTransactionsFilters';
import Container from './components/Container';
import Illustration from './components/Illustration';
import Title from './components/Title';
import useTransactionInitiator from './hooks/useTransactionInitiator';
import locales from './locales';
import * as S from './styles';

const EmptyState: FC = () => {
  const { canAccessFeature } = useCanCompanyUserAccessFeature();
  const { companyProfileId, companyUserId } = useCompanyContext();
  const { filters } = useTransactionsFilters();
  const { company, ...companyQuery } = useCompanyProfile(companyProfileId);
  const { loading: loadingBankAccountId, mainBankAccountId } =
    useMainBankAccountId();

  const { initiator, ...initiatorQuery } = useTransactionInitiator({
    companyUserId: filters.companyUserId,
  });

  if (companyQuery.loading || initiatorQuery.loading || !company) {
    return (
      <Container>
        <Loader />
      </Container>
    );
  }

  if (
    canAccessFeature({ name: FeatureName.Accountingexport }) &&
    canAccessFeature({ name: FeatureName.BankTransactions }) &&
    !canAccessFeature({ name: FeatureName.Paymentcards })
  ) {
    // accountant
    return (
      <Container>
        <Illustration image={TeapotImg} />
        <Text>
          {locales.formatString(locales.accountant.subtitle, {
            companyName: company.legalName,
          })}
        </Text>
      </Container>
    );
  }

  if (
    canAccessFeature({
      name: FeatureName.BankTransactions,
      where: {
        or: [
          {
            field: FeatureFilterField.User,
            values: [UserFilterValues.Self],
          },
        ],
      },
    })
  ) {
    // employee
    return (
      <Container>
        <Illustration />
        <Title>{locales.employee.title}</Title>
        <Text>
          {locales.formatString(locales.employee.subtitle, {
            companyName: company?.legalName ?? '',
          })}
        </Text>
      </Container>
    );
  }

  const initiatorFullname = `${initiator?.firstName}\xA0${initiator?.lastName}`;
  const isInitiator = companyUserId === initiator?.companyUserId;

  if (initiator) {
    // inspecting a companyUser's transactions
    return (
      <Container>
        <Illustration />
        <Title>
          {isInitiator
            ? locales.isInitiator.title
            : locales.formatString(locales.companyUser.title, {
                name: initiatorFullname,
              })}
        </Title>
        <Text>
          {isInitiator
            ? locales.isInitiator.subtitle
            : locales.formatString(locales.companyUser.subtitle, {
                name: initiatorFullname,
              })}
        </Text>
      </Container>
    );
  }

  const getHelpText = () => {
    if (
      canAccessFeature({ name: FeatureName.BankTransactions }) &&
      canAccessFeature({ name: FeatureName.PaymentcardsOrder })
    ) {
      return locales.helpText.allTransactions;
    }

    if (
      canAccessFeature({
        name: FeatureName.RequestPaymentcards,
      })
    ) {
      return locales.helpText.onlyOwnTransactions;
    }

    return locales.formatString(locales.helpText.default, {
      companyName: company?.legalName || '',
    });
  };

  return (
    <S.Container>
      <Image alt="" height="90px" role="presentation" src={safeImg} />

      <Text marginBottom="space-64" marginTop="space-24" variant="light">
        {getHelpText()}
      </Text>

      {canAccessFeature({ name: FeatureName.BankInfo }) ? (
        <S.IbanPreviewWrapper>
          <IbanPreview
            bankAccountId={mainBankAccountId}
            loading={loadingBankAccountId}
          />
        </S.IbanPreviewWrapper>
      ) : null}

      {canAccessFeature({
        name: FeatureName.RequestPaymentcards,
      }) ? (
        <Button
          as={Link}
          icon="card-recto"
          iconPosition="left"
          isExpanded
          textAlign="center"
          to={routes.requestNew}
        >
          {locales.requestNewCard}
        </Button>
      ) : null}
    </S.Container>
  );
};

export default EmptyState;
